import { Model } from "@/stores/AbstractBase.store";
import type { GoalSourceContentItem } from "@/utils/GoalSource";
import type { Pupil } from "./Pupil.model";
import { Evaluation } from "./Evaluation.model";
import { AbstractHasBufferedUpdates } from "@/models/AbstractHasBufferedUpdates.model.ts";

export class Goal extends AbstractHasBufferedUpdates implements Model {


	public id : string = '';
	public source ?: string;
	public sourceId : string | null = null;
	public sourceParentPath ?: string;
	public sourcePathId ?: string | null = null;
	public title ?: string;
	public customTitle ?: string | null = null;
	public info: string | null = null;
	public tags: string[] = [];
	public color: string | null = null;	// a goal can adopt a color from higher level group if available
	// public importedBy: null;

	public goalSourceStructure: GoalSourceContentItem | undefined;
	public pupilEvaluationMap: { [pupilId: string]: Evaluation[] } = {};

	public goalCollectionId: string | null = null; // the collection this goal belongs to and was selected from, if any

	/**
	 * Link state, not goal state: whether this goal is muted on the lesson plan or theme
	 * it was loaded through. Only meaningful for subgoals (MIA/THB elements always travel
	 * with their parent; muting is how a teacher leaves one out of a lesson). Defaults to
	 * false so an API that does not send it yet still works.
	 */
	public muted: boolean = false;

	/**
	 * Set by the API on a parent it added to an `unplanned` result only because one of
	 * its subgoals is unplanned (muted everywhere). Read-only context for the client.
	 */
	public partiallyPlanned: boolean = false;

	get fullPath(): string {
		let path = '';
		if (this.sourceParentPath) {
			path = this.sourceParentPath + '.';
		}

		if (this.sourcePathId) {
			path += this.sourcePathId;
		} else {
			path += 'x';
		}

		return path;
	}

	isSubgoal(): boolean {
		return this.sourcePathId === null;
	}

	/**
	 * Create a Goal object based on data coming from one of our own JSON curriculum files
	 */
	static mapFromStaticGoalSourceData(data: any, sourceId: string): Goal {
		let g = new Goal();
		g.source = sourceId;
		g.title = data.title;
		const splitPath = data.path.split('.');
		g.sourceParentPath = splitPath.slice(0, -1).join('.');
		g.sourcePathId = splitPath.pop();
		g.info = data.info;
		if(data.meta) {
			if(data.meta.tags) {
				g.tags = data.meta.tags;
			}
		}
		return g;
	}

	static createFromApiData(data: any): Goal {
		return (new Goal()).hydrateWithApiData(data);
	}

	static mapFromServer(data: any): Goal {
		console.warn('Goal.mapFromServer() is deprecated, use createFromApiData() instead');
		return Goal.createFromApiData(data);
	}

	setFromServerData(data: any): Goal {
		console.warn('Goal.setFromServerData() is deprecated, use hydrateWithApiData() instead');
		return this.hydrateWithApiData(data);
	}

	hydrateWithApiData(data: any) {
		this.id = data.id;
		this.source = data.source;
		this.sourceId = data.sourceId;
		this.sourceParentPath = data.parentPath;
		this.sourcePathId = data.pathId;
		this.title = data.title;
		this.customTitle = data.customTitle;
		this.color = data.color;
		this.info = data.info;
		this.muted = data.muted === true;
		this.partiallyPlanned = data.partiallyPlanned === true;

		if (data.tags) {
			this.tags = data.tags.map((tag: any) => {
				return tag.name;
			});
		}

		if (data.evaluations) {
			data.evaluations.forEach((evaluation: any) => {
				this.addEvaluation(evaluation.pupil.id, Evaluation.createFromApiData(evaluation));
			});
		}

		// Do we have a goal collection? In that case, the goal is part of a collection.
		// As goal collections are limited to one collection per classroom, we can use the first goalCollection id
		// and use that for - for example - the evaluations.
		if (data.goalCollections && data.goalCollections.length > 0) {
			this.goalCollectionId = data.goalCollections[0].id;
		} else {
			this.goalCollectionId = null;
		}

		return this;
	}

	addEvaluation(pupilId: string, Evaluation: Evaluation) {
		if (!this.pupilEvaluationMap[pupilId]) {
			this.pupilEvaluationMap[pupilId] = [];
		}
		this.pupilEvaluationMap[pupilId].push(Evaluation);
	}

	removeEvaluation(pupilId: string, Evaluation: Evaluation) {
		if (!this.pupilEvaluationMap[pupilId]) {
			return;
		}

		this.pupilEvaluationMap[pupilId] = this.pupilEvaluationMap[pupilId].filter((evaluation: Evaluation) => {
			return evaluation.id !== Evaluation.id;
		});
	}

	setFromModel(goal: Goal) {

		Object.assign(this, goal);

	}

	asApiDataObject() {
		return {
			id: this.id,
			source: this.source,
			sourceId: this.sourceId,
			parentPath: this.sourceParentPath,
			pathId: this.sourcePathId,
			title: this.title,
			customTitle: this.customTitle,
			color: this.color,
			info: this.info,
			muted: this.muted,
			tags: this.tags.map((tag) => {
				return {
					name: tag,
					type: 'goal'
				}
			}),
			goalCollection: this.goalCollectionId ? {
				id: this.goalCollectionId
			} : null,
		};
	}

	getServerData() {
		console.warn('Goal.getServerData() is deprecated, use asApiDataObject() instead');
		return this.asApiDataObject();
	}

	getLinkData() {
		if (this.id) {
			return {
				id: this.id,
				goalCollection: this.goalCollectionId ? {
					id: this.goalCollectionId
				} : null,
				muted: this.muted,
			};
		} else {
			return this.asApiDataObject();
		}
	}

	public equals(other: Goal): boolean {
		return JSON.stringify(this.asApiDataObject()) === JSON.stringify(other.asApiDataObject());
	}

	hasEvaluation(pupil: Pupil): boolean {
		return pupil.id !== null 
			&& typeof(this.pupilEvaluationMap[pupil.id]) !== 'undefined'
			&& this.pupilEvaluationMap[pupil.id].length > 0;
	}

	getEvaluations(pupil: Pupil): Evaluation[] {
		if (!pupil.id || typeof(this.pupilEvaluationMap[pupil.id]) === 'undefined') {
			return [];
		}

		return this.pupilEvaluationMap[pupil.id] || [];
	}

	getEvaluation(pupil: Pupil): Evaluation | null {
		
		const evaluations = this.getEvaluations(pupil);
		if (evaluations.length === 0) {
			return null;
		}
		return evaluations[evaluations.length - 1];

	}

	// gets evaluations in a given date range like last year etc.
	getEvaluationInRange(pupil: Pupil, start?: Date | null, end?: Date | null): Evaluation | null {
		let evaluations = this.getEvaluations(pupil);
		if (evaluations.length === 0) {
			return null;
		}

		if (start || end) {
			evaluations = evaluations.filter((evaluation) => {
				if (!evaluation.date) {
					return false;
				}
				if (start && evaluation.date < start) {
					return false;
				}
				if (end && evaluation.date > end) {
					return false;
				}
				return true;
			});
		}

		if (evaluations.length === 0) {
			return null;
		}

		// most recent within range
		return evaluations.reduce((latest, current) =>
			(current.date && latest.date && current.date > latest.date) ? current : latest
		);
	}

	get imported(): boolean {
		return this.id !== null;
	}

	clone(): Goal {
		let clonedInstance = Object.assign(
			Object.create(
				Object.getPrototypeOf(this)
			),
			this
		);
		return clonedInstance;
	}

}
